/**
 * The roster's `give` / `show` arm (D235, actors round B2) — the sentence
 * with NOBODY in the recipient's slot, or a THING there.
 *
 * @remarks
 * The whole act lives one layer up: a body after `to` is the actor
 * intercept's (`narrow-actor-aimed.ts`), which mints the `give` / `show`
 * action the handler reads `giveInteractions` for. What reaches this arm is
 * the remainder, and it is answered without a handler, because none could
 * do anything with it:
 *
 * - **No recipient, somebody who takes gifts somewhere in the game:** a
 *   QUESTION — `missing_recipient`, which D37 parks so a bare name answers
 *   it ("Give the film to whom?" — "the woman"). The affordance is the same
 *   one HELP reads (D229): in a game where nobody takes a thing, the
 *   question would be D39's trap, a question no answer can satisfy.
 * - **No recipient and nobody to give to, or a thing as the recipient:**
 *   D47's refusal, in the GIFT's own words when its pack wrote
 *   `unsupportedVerbNarratives.give` (Drumline's compass answers `give the
 *   compass to the bush` and `… to Sylvain` apart by `indirect_object_is`,
 *   D219) — else the `unsupportedIntentOnTarget` slot. The shape every pack
 *   got while the verb was unsupported, kept: a verb the engine learned to
 *   mechanize on a body must not change what a thing says.
 *
 * A room after `to` never arrives (the room intercept claims a room named
 * in any slot), and the hands do not either (the body intercept).
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { itemIdForEntityRef } from '../entities/entity-ref.js';
import { gameAnswersDirective } from './directive-affordances.js';
import { rejected } from './narrow-parsed-command.js';
import { refuseUnsupportedIntent } from './narrow-verb-answers.js';

/**
 * Narrow a gift verb whose recipient is nobody, or a thing.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param intentText - `give` or `show`, as the vocabulary names it
 * @returns The typed refusal — a question, or D47's answer
 */
export const narrowGiveCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	intentText: string,
): CommandNarrowingOutcome => {
	if (command.directObjectRef === undefined) {
		return rejected(bundle, state, { detail: `${intentText} arrived without a direct object`, type: 'malformed_command' }, [
			speakSlot(bundle, state, 'malformedCommand'),
		]);
	}

	const recipientRef = command.indirectObjectRef ?? command.toolRef;
	const itemId = itemIdForEntityRef(command.directObjectRef);

	if (recipientRef === undefined && gameAnswersDirective(intentText, bundle.contentPack, bundle.compiledLexicon.movementIntent.canonicalToken)) {
		return rejected(bundle, state, { intentToken: intentText, itemId, type: 'missing_recipient' }, [
			speakSlot(bundle, state, 'missingRecipient', {
				intent: command.intentSurface.toUpperCase(),
				itemName: requireItem(bundle, itemId).name,
				verb: command.intentSurface.toLowerCase(),
			}),
		]);
	}

	return refuseUnsupportedIntent(command, bundle, intentText, state);
};
